import { Link } from 'react-router-dom';
import { ArrowUpRight, Award, Sparkles } from 'lucide-react';
import SectionMarker from '@/components/sections/SectionMarker';
import { AI_CERTIFICATIONS, PHOTOS, PUBLICATIONS, BOOK, DOCUMENTARY, CEO_INTELLIGENCE } from '@/constants/content';
import { usePageMeta } from '@/lib/usePageMeta';

export default function Credentials() {
  usePageMeta({
    title: 'Credentials · Certifications, Awards, Training & Accomplishments | Toia Wallace',
    description:
      'The full record of Toia Wallace, verified AI certifications, professional training, awards, speaking, publications, and memberships. A page that grows with her career.',
    path: '/credentials',
    image: PHOTOS.about,
  });

  return (
    <div className="bg-ivory">
      {/* HERO */}
      <section className="pt-40 lg:pt-56 pb-16 lg:pb-24">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16 items-end">
          <div className="lg:col-span-8">
            <div className="eyebrow text-taupe mb-8">The Record · Certifications, Awards, Training, Accomplishments</div>
            <h1 className="font-serif-display text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.94] tracking-tightest text-matte">
              The <em className="italic text-taupe">credentials</em>
              <br />
              behind the work.
            </h1>
          </div>
          <div className="lg:col-span-4">
            <p className="text-matte/70 leading-[1.85] text-[15px] max-w-md">
              A living record of professional development, verified certifications, awards,
              training, speaking, publications, and memberships. This page grows with the career,
              not with the redesign.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 01 · PROFESSIONAL CERTIFICATIONS */}
      <section className="py-16 lg:py-24 bg-white" data-testid="credentials-professional-certifications">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
          <div className="grid lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-5">
              <SectionMarker number="01" label="Professional Certifications" />
              <h2 className="mt-8 font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tightest leading-[1.02] text-matte">
                Verified AI credentials.
              </h2>
            </div>
            <p className="lg:col-span-7 text-matte/70 leading-[1.85]">
              A professional foundation in artificial intelligence, accredited by the AI Institute
              of Professional Development and recognized by the International Board of Professional
              AI Accreditation (IBAI). Each credential is independently verifiable through CredsVerse.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {AI_CERTIFICATIONS.map((c, i) => (
              <a
                key={c.title}
                href={c.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`credentials-certification-${i}`}
                className="group relative bg-ivory rounded-2xl p-8 md:p-10 border border-taupe/25 hover:border-gold/60 hover:bg-white transition-[border-color,background-color,transform] duration-500 hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-12 h-12 rounded-full border border-gold/50 grid place-items-center">
                    <Award size={18} strokeWidth={1.4} className="text-gold" />
                  </div>
                  <div className="font-label text-[10px] tracking-[0.32em] uppercase text-gold">{c.level}</div>
                </div>
                <div className="font-label text-[10px] tracking-[0.28em] uppercase text-taupe mb-3">Verified Credential</div>
                <h3 className="font-serif-display text-2xl md:text-3xl leading-tight tracking-tighter text-matte mb-6 group-hover:text-gold transition-colors duration-500">
                  {c.title}
                </h3>
                <div className="hairline-gold h-px w-10 mb-6" aria-hidden />
                <dl className="space-y-3 text-[13px] leading-relaxed">
                  <div>
                    <dt className="font-label text-[9px] tracking-[0.28em] uppercase text-taupe mb-1">Accredited By</dt>
                    <dd className="text-matte/80">{c.accreditor}</dd>
                  </div>
                  <div>
                    <dt className="font-label text-[9px] tracking-[0.28em] uppercase text-taupe mb-1">Accreditation</dt>
                    <dd className="text-matte/80">{c.board}</dd>
                  </div>
                  <div>
                    <dt className="font-label text-[9px] tracking-[0.28em] uppercase text-taupe mb-1">Program</dt>
                    <dd className="text-matte/80 font-mono text-[12px]">{c.program}</dd>
                  </div>
                </dl>
                <div className="mt-8 inline-flex items-center gap-2 font-label text-[10px] tracking-[0.28em] uppercase text-matte group-hover:text-gold transition-colors">
                  Verify Credential
                  <ArrowUpRight size={14} strokeWidth={1.4} />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 02 · AWARDS & RECOGNITION */}
      <section className="py-16 lg:py-24 bg-ivory" data-testid="credentials-awards">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
          <div className="grid lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-5">
              <SectionMarker number="02" label="Awards & Recognition" />
              <h2 className="mt-8 font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tightest leading-[1.02] text-matte">
                Recognized <em className="italic text-taupe">quietly.</em>
              </h2>
            </div>
            <p className="lg:col-span-7 text-matte/70 leading-[1.85]">
              A record of recognitions received across entrepreneurship, healthcare, and business.
              This section is intentionally reserved for verifiable awards. It will grow as they arrive.
            </p>
          </div>

          <div className="rounded-2xl border border-taupe/25 bg-white p-10 md:p-14 text-center">
            <Sparkles size={20} strokeWidth={1.4} className="mx-auto text-gold mb-6" />
            <div className="eyebrow text-taupe mb-4">Coming Soon</div>
            <p className="font-serif-display text-2xl md:text-3xl italic text-matte leading-snug tracking-tight max-w-2xl mx-auto">
              Publicly announced awards and recognitions will be added here as they are received.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 03 · PROFESSIONAL TRAINING */}
      <section className="py-16 lg:py-24 bg-white" data-testid="credentials-training">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
          <div className="grid lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-5">
              <SectionMarker number="03" label="Professional Training" />
              <h2 className="mt-8 font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tightest leading-[1.02] text-matte">
                Ongoing training.
              </h2>
            </div>
            <p className="lg:col-span-7 text-matte/70 leading-[1.85]">
              Continuous professional development across artificial intelligence, business strategy,
              healthcare operations, product development, and executive leadership. This record is
              maintained internally and highlighted here when applicable.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              'Artificial Intelligence',
              'AI Governance & Compliance',
              'AI Business Integration',
              'AI Marketing & Communications',
              'Healthcare Operations',
              'Executive Leadership',
              'SaaS Product Development',
              'Founder-Led Growth',
            ].map((t, i) => (
              <div key={t} className="bg-ivory rounded-2xl p-6 border border-taupe/20 flex items-start gap-3">
                <span className="font-label text-[10px] tracking-widest uppercase text-taupe pt-1">{String(i + 1).padStart(2, '0')}</span>
                <span className="font-serif-display text-lg text-matte leading-tight tracking-tight">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 04 · SPEAKING & PRESENTATIONS */}
      <section className="py-16 lg:py-24 bg-matte text-ivory relative overflow-hidden" data-testid="credentials-speaking">
        <div className="absolute inset-0 opacity-20 noise-grain pointer-events-none" aria-hidden />
        <div className="relative mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
          <div className="grid lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-5">
              <div className="eyebrow text-gold mb-8">04 · Speaking & Presentations</div>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tightest leading-[1.02]">
                On the record.
              </h2>
            </div>
            <p className="lg:col-span-7 text-ivory/75 leading-[1.85]">
              Speaking engagements, keynotes, and featured presentations across AI, entrepreneurship,
              healthcare, and business transformation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <a
              href={CEO_INTELLIGENCE.summit.url}
              target="_blank"
              rel="noreferrer"
              className="group bg-ivory/5 border border-ivory/20 hover:border-gold/50 rounded-2xl p-8 md:p-10 transition-colors"
              data-testid="credentials-speaking-ai-summit"
            >
              <div className="eyebrow text-gold mb-4">Featured Presentation</div>
              <h3 className="font-serif-display text-2xl md:text-3xl leading-tight tracking-tight mb-3 group-hover:text-gold transition-colors">
                CEO Intelligence: How to Build a Business in 1 Hour with AI
              </h3>
              <p className="text-ivory/70 text-sm leading-relaxed mb-6">
                Presented live at the AI in Action Virtual Summit.
              </p>
              <div className="inline-flex items-center gap-2 font-label text-[10px] tracking-[0.28em] uppercase text-ivory group-hover:text-gold transition-colors">
                Watch on YouTube
                <ArrowUpRight size={14} strokeWidth={1.4} />
              </div>
            </a>

            <div className="bg-ivory/5 border border-ivory/20 rounded-2xl p-8 md:p-10">
              <div className="eyebrow text-gold mb-4">Speaking Topics</div>
              <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px] text-ivory/85 leading-relaxed">
                {['Artificial Intelligence', 'Entrepreneurship', 'Business Strategy', 'Founder Mindset', 'Healthcare Entrepreneurship', 'Innovation & Technology', "Women's Leadership", 'Building with AI'].map((t) => (
                  <li key={t} className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-gold" aria-hidden />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 05 · PUBLICATIONS & MEDIA */}
      <section className="py-16 lg:py-24 bg-ivory" data-testid="credentials-publications">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
          <div className="grid lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-5">
              <SectionMarker number="05" label="Publications & Media" />
              <h2 className="mt-8 font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tightest leading-[1.02] text-matte">
                On the page.
              </h2>
            </div>
            <p className="lg:col-span-7 text-matte/70 leading-[1.85]">
              Editorial features, interviews, and published works documenting the journey. See
              the Media page for the full record; a curated sample lives below.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PUBLICATIONS.slice(0, 3).map((p, i) => (
              <a
                key={p.url}
                href={p.url}
                target="_blank"
                rel="noreferrer"
                className="group bg-white rounded-2xl p-8 md:p-10 luxury-shadow border border-transparent hover:border-gold/40 transition-[transform,border-color] duration-500 hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="eyebrow text-taupe">{String(i + 1).padStart(2, '0')}</div>
                  <div className="font-label text-[10px] tracking-widest uppercase text-matte/50">{p.year}</div>
                </div>
                <div className="font-serif-display italic text-3xl md:text-4xl tracking-tighter text-matte mb-4 group-hover:text-gold transition-colors">
                  {p.name}
                </div>
                <p className="text-[14px] text-matte/70 leading-relaxed">{p.title}</p>
              </a>
            ))}
          </div>

          <div className="mt-16 grid md:grid-cols-2 gap-6">
            <a
              href={BOOK.url}
              target="_blank"
              rel="noreferrer"
              className="group bg-white rounded-2xl p-8 md:p-10 border border-taupe/20 hover:border-gold/50 transition-colors flex items-center gap-8"
              data-testid="credentials-book"
            >
              <img src={BOOK.cover} alt="Faith Got Me Here book cover" className="w-24 md:w-32 h-auto rounded-md shadow-xl" loading="lazy" />
              <div className="flex-1">
                <div className="eyebrow text-taupe mb-3">Published Author</div>
                <div className="font-serif-display text-2xl md:text-3xl text-matte tracking-tight leading-tight group-hover:text-gold transition-colors">
                  {BOOK.title}
                </div>
                <div className="mt-2 font-serif-display italic text-taupe text-base leading-snug">{BOOK.subtitle}</div>
                <div className="mt-4 inline-flex items-center gap-2 font-label text-[10px] tracking-[0.28em] uppercase text-matte group-hover:text-gold transition-colors">
                  Buy on Amazon
                  <ArrowUpRight size={12} strokeWidth={1.4} />
                </div>
              </div>
            </a>

            <a
              href={DOCUMENTARY.url}
              target="_blank"
              rel="noreferrer"
              className="group bg-white rounded-2xl p-8 md:p-10 border border-taupe/20 hover:border-gold/50 transition-colors"
              data-testid="credentials-documentary"
            >
              <div className="eyebrow text-taupe mb-3">Founder Documentary</div>
              <div className="font-serif-display text-2xl md:text-3xl text-matte tracking-tight leading-tight group-hover:text-gold transition-colors">
                {DOCUMENTARY.title}
              </div>
              <p className="mt-3 text-matte/70 text-[14px] leading-relaxed">
                A documented look at an earlier chapter of the entrepreneurial journey.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 font-label text-[10px] tracking-[0.28em] uppercase text-matte group-hover:text-gold transition-colors">
                Watch on YouTube
                <ArrowUpRight size={12} strokeWidth={1.4} />
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 06 · PROFESSIONAL MEMBERSHIPS */}
      <section className="py-16 lg:py-24 bg-white" data-testid="credentials-memberships">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
          <div className="grid lg:grid-cols-12 gap-8 mb-10">
            <div className="lg:col-span-5">
              <SectionMarker number="06" label="Professional Memberships" />
              <h2 className="mt-8 font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tightest leading-[1.02] text-matte">
                Where I belong.
              </h2>
            </div>
            <p className="lg:col-span-7 text-matte/70 leading-[1.85]">
              Industry memberships, professional societies, and communities. This section is
              reserved and will populate as memberships are formalized.
            </p>
          </div>

          <div className="rounded-2xl border border-taupe/25 bg-ivory p-10 md:p-14 text-center">
            <div className="eyebrow text-taupe mb-4">Coming Soon</div>
            <p className="font-serif-display text-2xl md:text-3xl italic text-matte leading-snug tracking-tight max-w-2xl mx-auto">
              Professional memberships and society affiliations will be documented here as they are established.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-matte text-ivory">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 text-center">
          <div className="eyebrow text-gold mb-8">A Living Record</div>
          <h2 className="font-serif-display text-4xl md:text-6xl tracking-tighter leading-tight">
            This page grows with the career, <em className="italic text-gold">not with the redesign.</em>
          </h2>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <Link to="/media" className="btn-primary bg-gold text-matte hover:bg-[#b8944e]" data-testid="credentials-cta-media">
              Explore Media & Press
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
            <Link to="/contact" className="btn-secondary border-ivory text-ivory hover:bg-ivory hover:text-matte" data-testid="credentials-cta-contact">
              Contact the Studio
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
